/**
 * Bốn khung hiển thị cho bốn trạng thái không-có-dữ-liệu của Bảng 3.3.
 * Trạng thái thứ năm ("có dữ liệu") là bố cục chuẩn của từng màn hình.
 *
 * Hai trạng thái rỗng dùng HAI thành phần khác nhau, câu chữ khác nhau và hành
 * động khác nhau — gộp lại là lỗi nghiệm thu.
 */
import type { ReactNode } from 'react';
import { vi } from '../ngon-ngu/vi';

/** Khung xương giữ đúng bố cục sẽ hiện ra, không dùng vòng xoay toàn trang. */
export function KhungXuong({
  soDong = 8,
  soCot = 6,
  taiLau = false,
}: {
  soDong?: number;
  soCot?: number;
  taiLau?: boolean;
}): ReactNode {
  return (
    <div className="tt-man-hinh tt-dang-tai" data-trang-thai="dang-tai" aria-busy="true">
      <p className="chi-cho-doc-man-hinh" role="status">
        {vi.trangThaiManHinh.dangTai}
      </p>
      <div className="khung-xuong" aria-hidden="true">
        {Array.from({ length: soDong }).map((_, i) => (
          <div className="khung-xuong__dong" key={i}>
            {Array.from({ length: soCot }).map((__, j) => (
              <span className="khung-xuong__o" key={j} />
            ))}
          </div>
        ))}
      </div>
      {taiLau ? <p className="tt-man-hinh__phu">{vi.trangThaiManHinh.dangTaiLau}</p> : null}
    </div>
  );
}

/** Rỗng vì CHƯA CÓ dữ liệu: nêu nguyên nhân và hành động khai báo mới. */
export function TrangThaiRong({
  moTa,
  nhanHanhDong,
  onHanhDong,
}: {
  moTa?: string;
  nhanHanhDong?: string;
  onHanhDong?: () => void;
}): ReactNode {
  return (
    <div className="tt-man-hinh tt-rong" data-trang-thai="rong">
      <h2>{vi.trangThaiManHinh.rongTieuDe}</h2>
      <p>{moTa ?? vi.trangThaiManHinh.rongMoTa}</p>
      {onHanhDong ? (
        <button type="button" className="nut nut--chinh" onClick={onHanhDong}>
          {nhanHanhDong ?? vi.trangThaiManHinh.rongHanhDong}
        </button>
      ) : null}
    </div>
  );
}

/** Rỗng vì BỘ LỌC: câu chữ và hành động khác hẳn trạng thái trên. */
export function TrangThaiRongLoc({ onXoaLoc }: { onXoaLoc: () => void }): ReactNode {
  return (
    <div className="tt-man-hinh tt-rong-loc" data-trang-thai="rong-loc">
      <h2>{vi.trangThaiManHinh.rongLocTieuDe}</h2>
      <p>{vi.trangThaiManHinh.rongLocMoTa}</p>
      <button type="button" className="nut" onClick={onXoaLoc}>
        {vi.trangThaiManHinh.rongLocHanhDong}
      </button>
    </div>
  );
}

/**
 * Lỗi: nêu việc hệ thống không làm được, mã lỗi để đối chiếu nhật ký, và nút thử
 * lại. Không hiển thị thông điệp lỗi kỹ thuật thô của máy chủ.
 */
export function TrangThaiLoi({
  loi,
  onThuLai,
}: {
  loi: { ma: string; thongBao: string; requestId?: string };
  onThuLai: () => void;
}): ReactNode {
  return (
    <div className="tt-man-hinh tt-loi" data-trang-thai="loi" role="alert">
      <h2>{vi.trangThaiManHinh.loiTieuDe}</h2>
      <p>{loi.thongBao}</p>
      <p className="tt-man-hinh__phu">
        {vi.trangThaiManHinh.loiMaLoi}: <code>{loi.ma}</code>
        {loi.requestId ? (
          <>
            {' · '}
            {vi.trangThaiManHinh.loiMaYeuCau}: <code>{loi.requestId}</code>
          </>
        ) : null}
      </p>
      <button type="button" className="nut nut--chinh" onClick={onThuLai}>
        {vi.trangThaiManHinh.loiThuLai}
      </button>
    </div>
  );
}
